import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { Truck, Gear, ChatCircle } from '@phosphor-icons/react';

function ToggleSwitch({ value, onToggle, disabled, testId }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      data-testid={testId}
      className={`relative inline-flex h-7 w-14 flex-shrink-0 items-center rounded-full transition-colors ${
        value ? 'bg-[#00C853]' : 'bg-[#808080]'
      } disabled:opacity-50`}
      aria-pressed={!!value}
    >
      <span className={`text-[10px] absolute ${value ? 'left-2 text-white font-bold' : 'right-2 text-white font-bold'}`}>
        {value ? 'ON' : 'OFF'}
      </span>
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
          value ? 'translate-x-8' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

function PoolSetting() {
  const [enabled, setEnabled] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/settings/pool')
      .then(r => setEnabled(!!r.data?.pool_enabled))
      .catch(() => setEnabled(true));
  }, []);

  const toggle = async () => {
    if (saving || enabled === null) return;
    setSaving(true);
    try {
      const next = !enabled;
      const { data } = await api.put('/admin/settings/pool', { pool_enabled: next });
      setEnabled(!!data.pool_enabled);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-[#E5E5E5] p-4 flex items-center justify-between gap-4" data-testid="settings-pool-row">
      <div className="flex items-start gap-3 min-w-0">
        <Truck size={22} className="text-[#002FA7] flex-shrink-0 mt-0.5" />
        <div className="min-w-0">
          <h3 className="text-sm font-bold uppercase tracking-widest text-[#0A0A0A]">Vehicle Pooling</h3>
          <p className="text-xs text-[#666] mt-1">
            When <strong>ON</strong>, new bookings are first offered to nearby active pilots within 2&nbsp;km for 3&nbsp;minutes before normal auto-allocation. When <strong>OFF</strong>, every booking goes directly to the regular allocation engine.
          </p>
        </div>
      </div>
      <ToggleSwitch value={enabled} onToggle={toggle} disabled={saving || enabled === null} testId="pool-toggle-btn" />
    </div>
  );
}

function MaxTripsSetting() {
  const [value, setValue] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/settings/max-trips')
      .then(r => setValue(r.data?.max_trips_per_pilot ?? 2))
      .catch(() => setValue(2));
  }, []);

  const handleChange = async (e) => {
    const next = parseInt(e.target.value, 10);
    if (saving || value === null) return;
    setSaving(true);
    setValue(next);
    try {
      const { data } = await api.put('/admin/settings/max-trips', { max_trips_per_pilot: next });
      setValue(data.max_trips_per_pilot);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-[#E5E5E5] p-4 flex items-center justify-between gap-4" data-testid="settings-max-trips-row">
      <div className="flex items-start gap-3 min-w-0">
        <Gear size={22} className="text-[#FF8C00] flex-shrink-0 mt-0.5" />
        <div className="min-w-0">
          <h3 className="text-sm font-bold uppercase tracking-widest text-[#0A0A0A]">Max Trips Per Pilot</h3>
          <p className="text-xs text-[#666] mt-1">
            How many simultaneous active trips a single pilot can hold. The auto-allocation engine will skip pilots already at this cap. Default is <strong>2</strong>.
          </p>
        </div>
      </div>
      <select
        value={value ?? 2}
        onChange={handleChange}
        disabled={saving || value === null}
        data-testid="max-trips-select"
        className="h-10 px-3 text-sm bg-white border border-[#E5E5E5] focus:outline-none focus:border-[#002FA7] disabled:opacity-50"
      >
        <option value={1}>1 trip</option>
        <option value={2}>2 trips</option>
        <option value={3}>3 trips</option>
        <option value={4}>4 trips</option>
      </select>
    </div>
  );
}

function WhatsAppStatsCard() {
  // Phase 78al — Last-30-day WhatsApp send stats. Surfaces sent / failed
  // / deduped counts per event so admin can spot template misuse and
  // chase whatapi.in if duplicates persist.
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await api.get(`/admin/settings/whatsapp/stats?days=${days}`);
        if (active) setStats(r.data);
      } catch { /* swallow — empty card is acceptable */ }
    })();
    return () => { active = false; };
  }, [days]);

  if (!stats) return null;
  const t = stats.totals || {};
  const pillCls = 'rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest';

  return (
    <div className="rounded-2xl border border-[#E5E5E5] bg-white p-4 space-y-3" data-testid="whatsapp-stats-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-bold uppercase tracking-widest text-[#0A0A0A]">WhatsApp send stats</h3>
        <select
          value={days}
          onChange={e => setDays(Number(e.target.value))}
          className="border border-[#E5E5E5] rounded-full px-3 py-1 text-xs bg-white"
          data-testid="whatsapp-stats-range"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={180}>Last 180 days</option>
        </select>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`${pillCls} bg-[#00C853] text-white`} data-testid="stats-total-sent">{t.sent || 0} sent</span>
        <span className={`${pillCls} bg-[#FF3333] text-white`} data-testid="stats-total-failed">{t.failed || 0} failed</span>
        <span className={`${pillCls} bg-[#7C3AED] text-white`} data-testid="stats-total-deduped">{t.deduped || 0} deduped</span>
        <span className={`${pillCls} bg-[#0A0A0A] text-white`}>{t.total || 0} total</span>
      </div>
      <div className="overflow-x-auto -mx-4">
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-widest text-[#666]">
            <tr className="bg-[#F8F8F8]">
              <th className="text-left px-4 py-1.5">Event</th>
              <th className="text-right px-2 py-1.5">Sent</th>
              <th className="text-right px-2 py-1.5">Failed</th>
              <th className="text-right px-2 py-1.5">Deduped</th>
              <th className="text-right px-4 py-1.5">Total</th>
            </tr>
          </thead>
          <tbody>
            {(stats.by_event || []).map(row => {
              const failRatio = row.total ? row.failed / row.total : 0;
              const dupRatio = row.total ? row.deduped / row.total : 0;
              return (
                <tr key={row.event} className="border-t border-[#F0F0F0]" data-testid={`stats-row-${row.event}`}>
                  <td className="px-4 py-1.5 font-mono">{row.event}</td>
                  <td className="px-2 py-1.5 text-right font-bold text-[#00C853]">{row.sent}</td>
                  <td className={`px-2 py-1.5 text-right font-bold ${failRatio > 0.1 ? 'text-[#FF3333]' : 'text-[#666]'}`}>{row.failed}</td>
                  <td className={`px-2 py-1.5 text-right font-bold ${dupRatio > 0.05 ? 'text-[#7C3AED]' : 'text-[#666]'}`}>{row.deduped}</td>
                  <td className="px-4 py-1.5 text-right">{row.total}</td>
                </tr>
              );
            })}
            {(!stats.by_event || stats.by_event.length === 0) && (
              <tr><td colSpan={5} className="px-4 py-4 text-center text-[#666]">No WhatsApp activity in this window.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-[#666] italic">
        High <span className="text-[#FF3333] font-bold">failed</span> = check whatapi.in webhook key & template name.
        High <span className="text-[#7C3AED] font-bold">deduped</span> = pilot or booking app is double-firing the lifecycle endpoint — open Live Tracking and observe.
      </p>
    </div>
  );
}

function WhatsAppMatrix() {
  const [data, setData] = useState(null);
  const [savingCell, setSavingCell] = useState('');
  const [savingMaster, setSavingMaster] = useState(false);

  const refresh = async () => {
    try { const r = await api.get('/admin/settings/whatsapp'); setData(r.data); }
    catch (e) { alert(e?.response?.data?.detail || 'Failed to load WhatsApp settings'); }
  };
  useEffect(() => { refresh(); }, []);

  const isEnabled = (adminId, eventKey) => {
    const v = data?.prefs?.[adminId]?.[eventKey];
    return v === undefined ? true : !!v;   // default ON
  };

  const toggleCell = async (adminId, eventKey) => {
    const cellKey = `${adminId}:${eventKey}`;
    if (savingCell === cellKey) return;
    const next = !isEnabled(adminId, eventKey);
    setSavingCell(cellKey);

    // Optimistic update.
    setData(prev => {
      if (!prev) return prev;
      const prefs = { ...(prev.prefs || {}) };
      prefs[adminId] = { ...(prefs[adminId] || {}), [eventKey]: next };
      return { ...prev, prefs };
    });

    try {
      await api.put('/admin/settings/whatsapp/pref', {
        admin_user_id: adminId, event: eventKey, enabled: next,
      });
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to save');
      refresh();
    } finally {
      setSavingCell('');
    }
  };

  const toggleMaster = async () => {
    if (!data || savingMaster) return;
    const next = !data.master;
    setSavingMaster(true);
    try {
      const r = await api.put('/admin/settings/whatsapp/master', { whatsapp_enabled: next });
      setData(prev => ({ ...prev, master: !!r.data.whatsapp_enabled }));
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to update');
    } finally {
      setSavingMaster(false);
    }
  };

  const summary = useMemo(() => {
    if (!data) return null;
    const totalCells = (data.admins?.length || 0) * (data.events?.length || 0);
    let off = 0;
    Object.values(data.prefs || {}).forEach(byEvent => {
      Object.values(byEvent || {}).forEach(v => { if (v === false) off += 1; });
    });
    return { total: totalCells, on: totalCells - off, off };
  }, [data]);

  if (!data) return <div className="bg-white border border-[#E5E5E5] p-4 text-sm text-[#666]" data-testid="whatsapp-matrix-loading">Loading WhatsApp settings…</div>;

  return (
    <div className="space-y-3" data-testid="whatsapp-matrix-section">
      {/* Master switch */}
      <div className="bg-white border border-[#E5E5E5] p-4 flex items-center justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <ChatCircle size={22} className="text-[#25D366] flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <h3 className="text-sm font-bold uppercase tracking-widest text-[#0A0A0A]">WhatsApp Notifications</h3>
            <p className="text-xs text-[#666] mt-1">
              Master switch.  When <strong>OFF</strong>, no admin receives any WhatsApp regardless of the matrix below.
              When <strong>ON</strong>, the matrix decides who gets which event.
            </p>
            {summary && (
              <p className="text-[10px] uppercase tracking-widest text-[#666] mt-2">
                <span className="font-bold text-[#0A0A0A]">{summary.on}</span> on · <span className="font-bold text-[#FF3333]">{summary.off}</span> off · {summary.total} total
              </p>
            )}
          </div>
        </div>
        <ToggleSwitch value={data.master} onToggle={toggleMaster} disabled={savingMaster} testId="whatsapp-master-toggle" />
      </div>

      {/* Matrix */}
      <div className="bg-white border border-[#E5E5E5] overflow-x-auto" data-testid="whatsapp-matrix">
        <table className="text-xs min-w-full">
          <thead className="bg-[#F3F3F3]">
            <tr>
              <th className="text-left px-3 py-2 sticky left-0 bg-[#F3F3F3] z-10 uppercase tracking-widest text-[10px] font-bold text-[#666]">User ↓ / Event →</th>
              {data.events.map(ev => (
                <th key={ev.key} className="px-2 py-2 text-[10px] font-bold uppercase tracking-tight text-[#666] whitespace-nowrap">
                  {ev.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.admins.length === 0 && (
              <tr><td colSpan={data.events.length + 1} className="px-3 py-6 text-center text-[#666]">No admins found.</td></tr>
            )}
            {data.admins.map((a, i) => (
              <tr
                key={a.id}
                className={a.is_virtual ? 'bg-[#FFF8E1] border-y-2 border-[#FFD700]' : (i % 2 ? 'bg-[#F8F8F8]' : '')}
              >
                <td className="px-3 py-2 sticky left-0 bg-inherit font-medium whitespace-nowrap">
                  <div className={`text-sm ${a.is_virtual ? 'text-[#B27300] font-bold uppercase tracking-widest' : 'text-[#0A0A0A]'}`}>
                    {a.name || a.mobile}
                    {a.is_virtual && <span className="ml-1.5 text-[9px] bg-[#FFD700] text-[#0A0A0A] rounded-full px-1.5 py-0.5">GROUP</span>}
                  </div>
                  <div className="text-[10px] text-[#666] font-mono">{a.mobile}</div>
                </td>
                {data.events.map(ev => {
                  const on = isEnabled(a.id, ev.key);
                  const cellKey = `${a.id}:${ev.key}`;
                  const busy = savingCell === cellKey;
                  return (
                    <td key={ev.key} className="px-2 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => toggleCell(a.id, ev.key)}
                        disabled={busy}
                        title={`${a.name} · ${ev.label}: ${on ? 'ON' : 'OFF'} (click to toggle)`}
                        data-testid={`wa-cell-${a.id}-${ev.key}`}
                        className={`w-7 h-7 flex items-center justify-center rounded-md transition-all ${
                          on ? 'bg-[#00C853] text-white' : 'bg-white border border-[#E5E5E5] text-[#999]'
                        } ${busy ? 'opacity-50' : 'hover:scale-110'}`}
                      >
                        {on ? '✓' : '×'}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminSettings() {
  return (
    <div data-testid="admin-settings" className="space-y-6">
      <div className="flex items-center gap-3">
        <Gear size={32} weight="bold" className="text-[#002FA7]" />
        <h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Settings</h1>
      </div>

      <p className="text-sm text-[#666]">Global toggles that affect how Carloo allocates and notifies trips.</p>

      <section className="space-y-3">
        <h2 className="text-xs uppercase tracking-widest text-[#666] font-bold">Allocation</h2>
        <PoolSetting />
        <MaxTripsSetting />
      </section>

      <section className="space-y-3">
        <h2 className="text-xs uppercase tracking-widest text-[#666] font-bold">Notifications</h2>
        <WhatsAppStatsCard />
        <WhatsAppMatrix />
      </section>
    </div>
  );
}
